import React, {useEffect, useRef} from 'react';
import {Notification} from '../navigation/page-layout';
import {RegionId, ServiceResource, useAuth} from '../hooks/use-auth';
import {translateErrorToReactNode} from '../common';
import PortalLoader from '../common/PortalLoader';
import * as QuickSightEmbedding from 'amazon-quicksight-embedding-sdk';
import {AppLayout, ContentLayout, Header, HelpPanel, Link} from '@amzn/awsui-components-react';
import {useGetDashboardUrlByDashboardName} from '../hooks/use-quicksight-api';
import {FileRejection, useDropzone} from 'react-dropzone';

export function ViewOteAnomalies(props: {pushNotification: (notification: Notification) => void}) {
    const auth = useAuth();
    const selectedRegion = RegionId.EU;

    const clientConfigs = auth.authInformation!.getEndpoints()[selectedRegion][ServiceResource.ForecastStoreInsights];

    function createErrorListener(header: string) {
        return (e: any) => {
            props.pushNotification({
                type: 'error',
                content: translateErrorToReactNode(e),
                header,
            });
        };
    }

    const {status: getDashboardUrlStatus, value: getDashboardUrlResponse} = useGetDashboardUrlByDashboardName(
        clientConfigs,
        {dashboardName: 'OTE Anomaly Detection'},
        true,
        createErrorListener('GetDashboardUrlByDashboardName failed'),
        [auth, selectedRegion]
    );

    const isLoading = getDashboardUrlStatus === 'idle' || getDashboardUrlStatus === 'pending';

    return (
        <AppLayout
            disableContentPaddings={true}
            content={
                <ContentLayout>
                    {isLoading ? (
                        <PortalLoader />
                    ) : (
                        <>
                            <OteAnomalyDashboard
                                key={selectedRegion}
                                url={getDashboardUrlResponse?.dashboardUrl || ''}
                                pushNotification={props.pushNotification}
                            />
                            <div style={{textAlign: 'center', padding: '0 1vw'}}>
                                <div style={{display: 'inline-block', textAlign: 'center'}}>
                                    <Header variant={'h1'}>UPLOAD EXCLUSIONS</Header>
                                </div>
                                <br />
                                <p
                                    style={{
                                        fontFamily: "'Amazon Ember', 'Helvetica Neue', Roboto, Arial, sans-serif",
                                    }}
                                >
                                    {'Export anomalies from the table above ' +
                                        '(⋮ menu → Export to CSV), ' +
                                        'mark rows for exclusion, then upload here.'}
                                </p>
                                <div
                                    style={{
                                        height: 350,
                                        marginTop: 50,
                                        marginBottom: 50,
                                    }}
                                >
                                    <div
                                        style={{
                                            marginLeft: '20%',
                                            marginRight: '20%',
                                            height: '100%',
                                            border: '2px solid black',
                                            display: 'table',
                                            width: '60%',
                                        }}
                                    >
                                        <OteExclusionDropzone pushNotification={props.pushNotification} />
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </ContentLayout>
            }
            tools={
                <HelpPanel header="OTE Anomaly Detection">
                    <p>
                        <Link
                            href="https://quip-amazon.com/OSkNAy4YvCuI/OTE-Exclusions-for-QC-MENA-NA"
                            external
                            externalIconAriaLabel="Opens in a new tab"
                        >
                            OTE Exclusions Design Doc
                        </Link>
                    </p>
                    <p>
                        <Link
                            href="https://quip-amazon.com/FFWjA53hrK3L/OTE-Exclusions-for-MENA-NA-Frontend-Implementation-REVISED"
                            external
                            externalIconAriaLabel="Opens in a new tab"
                        >
                            Frontend Implementation (Revised)
                        </Link>
                    </p>
                </HelpPanel>
            }
            navigationHide
        />
    );
}

interface OteAnomalyDashboardProps {
    url: string;
    pushNotification: (notification: Notification) => void;
}

function OteAnomalyDashboard(props: OteAnomalyDashboardProps) {
    const ref = useRef<HTMLDivElement | null>(null);

    function onError(error: any) {
        props.pushNotification({
            type: 'error',
            header: 'Embedding Quicksight Dashboard failed',
            content: translateErrorToReactNode(error),
        });
    }

    useEffect(() => {
        if (!ref.current || !props.url) return;
        const dashboard = QuickSightEmbedding.embedDashboard({
            url: props.url,
            container: ref.current,
            locale: 'en-US',
            footerPaddingEnabled: true,
            scrolling: 'yes',
            parameters: {},
        });
        dashboard.on('error', onError);
    }, []);

    return <div id="embeddingContainer" ref={ref} />;
}

function OteExclusionDropzone(props: {pushNotification: (notification: Notification) => void}) {
    const onDrop = (acceptedFiles: File[], fileRejections: FileRejection[]) => {
        if (fileRejections.length > 0) {
            props.pushNotification({
                type: 'error',
                content: 'File rejected. Please upload a .csv or .xlsx file.',
            });
            return;
        }
        if (acceptedFiles.length > 0) {
            props.pushNotification({
                type: 'info',
                content: `File "${acceptedFiles[0].name}" received. Backend processing not yet wired up.`,
            });
        }
    };

    const {getRootProps, getInputProps} = useDropzone({
        onDrop,
        maxFiles: 1,
        accept: '.csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv',
    });

    return (
        <div {...getRootProps()} style={{height: '100%', width: '100%', display: 'table-cell', verticalAlign: 'middle'}}>
            <input {...getInputProps()} />
            <p
                style={{
                    fontFamily: "'Amazon Ember', 'Helvetica Neue', Roboto, Arial, sans-serif",
                }}
            >
                Drag and drop some files here, or click to select files.
            </p>
        </div>
    );
}
